import { Route } from '@angular/router';
import { ShellComponent } from './layout/shell/shell.component';
import { PortalShellComponent } from './layout/portal-shell/portal-shell.component';
import { authGuard } from './guards/auth.guard';
import { moduleAccessGuard, reportRedirectGuard } from './guards/module-access.guard';
import { featureFlagGuard } from './guards/feature-flag.guard';
import { dashboardRedirectGuard } from './guards/dashboard-redirect.guard';
import { portalAccessGuard } from './guards/portal-access.guard';
import { staffAccessGuard } from './guards/staff-access.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';

export const appRoutes: Route[] = [
  {
    // Public root — marketing/signup for prospects without a tenant. The
    // landing component itself decides whether to render, redirect to
    // /launcher (tenant resolves from host), or push authenticated users
    // to their dashboard.
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/signup/signup-landing.component').then(
        (m) => m.SignupLandingComponent,
      ),
  },
  {
    path: 'signup',
    loadComponent: () =>
      import('./modules/signup/signup-setup.component').then(
        (m) => m.SignupSetupComponent,
      ),
    pathMatch: 'full',
  },
  {
    path: 'signup/check-email',
    loadComponent: () =>
      import('./modules/signup/signup-check-email.component').then(
        (m) => m.SignupCheckEmailComponent,
      ),
  },
  {
    path: 'signup/expired',
    loadComponent: () =>
      import('./modules/signup/signup-expired.component').then(
        (m) => m.SignupExpiredComponent,
      ),
  },
  {
    path: 'signup/member/:token',
    loadComponent: () =>
      import('./modules/signup-member/signup-member.component').then(
        (m) => m.SignupMemberComponent,
      ),
  },
  {
    path: 'signup/complete',
    loadComponent: () =>
      import('./modules/signup-complete/signup-complete.component').then(
        (m) => m.SignupCompleteComponent,
      ),
  },
  {
    path: 'pay',
    loadComponent: () =>
      import('./modules/pay/pay.component').then((m) => m.PayComponent),
  },
  {
    path: 'launcher',
    loadComponent: () =>
      import('./modules/launcher/launcher.component').then(
        (m) => m.LauncherComponent,
      ),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./modules/auth-callback/auth-callback.component').then(
        (m) => m.AuthCallbackComponent,
      ),
  },
  {
    path: 'auth/handoff',
    loadComponent: () =>
      import('./modules/auth-handoff/auth-handoff.component').then(
        (m) => m.AuthHandoffComponent,
      ),
  },
  {
    path: 'switch-portal',
    loadComponent: () =>
      import('./modules/switch-portal/switch-portal.component').then(
        (m) => m.SwitchPortalComponent,
      ),
  },
  {
    path: 'accept-invite',
    loadComponent: () =>
      import('./modules/accept-invite/accept-invite.component').then(
        (m) => m.AcceptInviteComponent,
      ),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./modules/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent,
      ),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./modules/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'portal',
    component: PortalShellComponent,
    canActivate: [portalAccessGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./modules/portal/portal-dashboard/portal-dashboard.component').then(
            (m) => m.PortalDashboardComponent,
          ),
      },
      {
        path: 'events',
        loadComponent: () =>
          import('./modules/portal/portal-events/portal-events.component').then(
            (m) => m.PortalEventsComponent,
          ),
      },
      {
        path: 'events/:id',
        loadComponent: () =>
          import('./modules/portal/portal-event-detail/portal-event-detail.component').then(
            (m) => m.PortalEventDetailComponent,
          ),
      },
      {
        path: 'announcements',
        loadComponent: () =>
          import('./modules/portal/portal-announcements/portal-announcements.component').then(
            (m) => m.PortalAnnouncementsComponent,
          ),
      },
      {
        path: 'announcements/:id',
        loadComponent: () =>
          import('./modules/portal/portal-content-detail/portal-content-detail.component').then(
            (m) => m.PortalContentDetailComponent,
          ),
      },
      {
        path: 'news',
        loadComponent: () =>
          import('./modules/portal/portal-news/portal-news.component').then(
            (m) => m.PortalNewsComponent,
          ),
      },
      {
        path: 'news/:id',
        loadComponent: () =>
          import('./modules/portal/portal-content-detail/portal-content-detail.component').then(
            (m) => m.PortalContentDetailComponent,
          ),
      },
      {
        path: 'content/:modelKey',
        loadComponent: () =>
          import('./modules/portal/portal-content-list/portal-content-list.component').then(
            (m) => m.PortalContentListComponent,
          ),
      },
      {
        path: 'content/:modelKey/:id',
        loadComponent: () =>
          import('./modules/portal/portal-content-detail/portal-content-detail.component').then(
            (m) => m.PortalContentDetailComponent,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./modules/portal/portal-profile/portal-profile.component').then(
            (m) => m.PortalProfileComponent,
          ),
      },
      {
        path: 'account',
        loadComponent: () =>
          import('./modules/portal/portal-account/portal-account.component').then(
            (m) => m.PortalAccountComponent,
          ),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./modules/portal/portal-users/portal-users.component').then(
            (m) => m.PortalUsersComponent,
          ),
      },
      {
        path: 'billing',
        children: [
          {
            path: 'invoices',
            loadComponent: () =>
              import('./modules/portal/portal-invoices/portal-invoices.component').then(
                (m) => m.PortalInvoicesComponent,
              ),
          },
          {
            path: 'payment-methods',
            loadComponent: () =>
              import('./modules/portal/portal-payment-methods/portal-payment-methods.component').then(
                (m) => m.PortalPaymentMethodsComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: '',
    component: ShellComponent,
    // Every child below is an authenticated admin surface — the public routes
    // (launcher, auth/callback, auth/handoff, signup, pay, accept-invite,
    // forgot/reset-password) are all declared above, outside this shell.
    // Guarding here captures the intended URL *before* the shell renders and
    // its first API call 401s, which is what lets an emailed deep link such as
    // /notification-settings survive the login round-trip.
    //
    // staffAccessGuard runs second, and ORDER MATTERS: authGuard settles
    // "signed in at all" first, so the portal check below never has to reason
    // about an anonymous visitor. It belongs on the shell rather than on a child
    // because it must cover EVERY admin URL — a member refreshing /dashboard or
    // /error?reason=no-access previously re-entered the shell unchecked.
    canActivate: [authGuard, staffAccessGuard],
    children: [
      {
        path: '',
        canActivate: [dashboardRedirectGuard],
        children: [], // Empty children, guard handles redirect
      },
      {
        path: 'error',
        loadComponent: () =>
          import('./errors/error.component').then((m) => m.ErrorComponent),
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('./errors/error.component').then((m) => m.ErrorComponent),
        data: {
          title: '404 - Page Not Found',
          message: 'The page you are looking for does not exist.',
        },
      },
      {
        path: 'system-admin',
        loadComponent: () =>
          import('@ams/dashboard').then(
            (m) => m.DynamicDashboardComponent,
          ),
      },
      {
        path: 'system-admin/tenants',
        loadComponent: () =>
          import(
            './modules/system-admin/tenants/tenants.component'
          ).then((m) => m.TenantsComponent),
      },
      {
        // MEMBORA's own product plans, the ones associations subscribe to. Distinct
        // from /memberships/plans, which is a tenant's own member plans.
        path: 'system-admin/plans',
        loadComponent: () =>
          import(
            './modules/system-admin/plans/system-admin-plans.component'
          ).then((m) => m.SystemAdminPlansComponent),
      },
      {
        path: 'system-admin/legal',
        loadComponent: () =>
          import(
            './modules/system-admin/legal/legal-admin.component'
          ).then((m) => m.LegalAdminComponent),
      },
      {
        path: 'system-admin/users',
        loadComponent: () =>
          import('./modules/users/users.component').then(
            (m) => m.UsersComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { module: 'tenant.staff', requiredLevel: 'view' },
      },
      {
        path: 'dashboard',
        canActivate: [moduleAccessGuard],
        // Any-of: tenant admins (tenant.dashboard) and chapter admins
        // (chapter.dashboard, in chapter context) both reach the dashboard.
        data: { modules: ['tenant.dashboard', 'chapter.dashboard'] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@ams/dashboard').then(
                (m) => m.DynamicDashboardComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('@ams/dashboard').then(
                (m) => m.DynamicDashboardComponent,
              ),
          },
        ],
      },
      {
        path: 'memberships',
        canActivate: [moduleAccessGuard],
        data: { modules: ['tenant.memberships', 'chapter.memberships'] },
        children: [
          {
            path: 'plans',
            loadComponent: () =>
              import('./modules/memberships/plans/plans.component').then(
                (m) => m.PlansComponent,
              ),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.plans', 'chapter.plans'] },
          },
          {
            path: 'accounts',
            loadComponent: () =>
              import('./modules/memberships/accounts/accounts.component').then(
                (m) => m.AccountsComponent,
              ),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'] },
          },
          {
            path: 'accounts/signup-links',
            loadComponent: () =>
              import(
                './modules/memberships/accounts/signup-links/signup-links.component'
              ).then((m) => m.SignupLinksComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'], requiredLevel: 'manage' },
          },
          {
            path: 'applications',
            loadComponent: () =>
              import(
                './modules/memberships/applications/applications.component'
              ).then((m) => m.ApplicationsComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'], requiredLevel: 'manage' },
          },
          {
            path: 'applications/:orgId',
            loadComponent: () =>
              import(
                './modules/memberships/applications/application-detail/application-detail.component'
              ).then((m) => m.ApplicationDetailComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'], requiredLevel: 'manage' },
          },
          {
            path: 'members/uploads',
            loadComponent: () =>
              import(
                './modules/memberships/members/upload-manager/upload-manager.component'
              ).then((m) => m.UploadManagerComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'], requiredLevel: 'manage' },
          },
          {
            path: 'members/uploads/new',
            loadComponent: () =>
              import(
                './modules/memberships/members/upload-wizard/upload-wizard.component'
              ).then((m) => m.UploadWizardComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'], requiredLevel: 'manage' },
          },
          {
            path: 'members/uploads/wizard/:conversationId',
            loadComponent: () =>
              import(
                './modules/memberships/members/upload-wizard/upload-wizard.component'
              ).then((m) => m.UploadWizardComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'], requiredLevel: 'manage' },
          },
          {
            path: 'members/uploads/jobs/:jobId',
            loadComponent: () =>
              import(
                './modules/memberships/members/upload-result/upload-result.component'
              ).then((m) => m.UploadResultComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'], requiredLevel: 'manage' },
          },
          {
            path: 'accounts/:id',
            loadComponent: () =>
              import(
                './modules/memberships/accounts/member-detail/member-detail.component'
              ).then((m) => m.MemberDetailComponent),
            canActivate: [moduleAccessGuard],
            data: { modules: ['tenant.members', 'chapter.members'] },
          },
          // Legacy redirects — preserve bookmarks while the rename rolls out.
          { path: 'members', redirectTo: 'accounts', pathMatch: 'full' },
          { path: 'members/signup-links', redirectTo: 'accounts/signup-links', pathMatch: 'full' },
          { path: 'members/:id', redirectTo: 'accounts/:id' },
        ],
      },
      {
        path: 'chapters',
        canActivate: [moduleAccessGuard],
        data: { module: 'tenant.chapters' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/chapters/chapters.component').then(
                (m) => m.ChaptersComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import(
                './modules/chapters/chapter-detail/chapter-detail.component'
              ).then((m) => m.ChapterDetailComponent),
          },
        ],
      },
      {
        path: 'events',
        loadComponent: () =>
          import('./modules/events/events.component').then(
            (m) => m.EventsComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { module: 'chapter.events' },
      },
      {
        path: 'events/:id',
        loadComponent: () =>
          import('./modules/events/event-detail/event-detail.component').then(
            (m) => m.EventDetailComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { module: 'chapter.events' },
      },
      {
        // Coupons (admin) — #21795. Route convention: /store/{screen-name}.
        path: 'store/coupons',
        loadComponent: () =>
          import('@ams/storefront/pages').then((m) => m.StoreCouponsComponent),
        canActivate: [moduleAccessGuard, featureFlagGuard],
        data: { module: 'tenant.storefront', featureFlag: 'storefront' },
      },
      {
        // Orders (admin) — #21791. Route convention: /store/{screen-name}.
        path: 'store/orders',
        loadComponent: () =>
          import('@ams/storefront/pages').then((m) => m.StoreOrdersComponent),
        canActivate: [moduleAccessGuard, featureFlagGuard],
        data: { module: 'tenant.storefront', featureFlag: 'storefront' },
      },
      {
        // Order detail (admin) — #21792. Reached directly by order id.
        path: 'store/orders/:id',
        loadComponent: () =>
          import('@ams/storefront/pages').then((m) => m.OrderDetailComponent),
        canActivate: [moduleAccessGuard, featureFlagGuard],
        data: { modules: ['tenant.storefront'], featureFlag: 'storefront' },
      },
      {
        path: 'calendar',
        loadComponent: () =>
          import('./modules/calendar/calendar-page.component').then(
            (m) => m.CalendarPageComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { module: 'chapter.events' },
      },
      {
        path: 'reports',
        children: [
          {
            path: '',
            canActivate: [reportRedirectGuard],
            children: [],
            pathMatch: 'full',
          },
          {
            path: 'membership',
            canActivate: [moduleAccessGuard],
            data: { module: 'tenant.membership-report' },
            loadComponent: () =>
              import(
                './modules/reports/membership/membership-report.component'
              ).then((m) => m.MembershipReportComponent),
          },
          {
            path: 'events',
            canActivate: [moduleAccessGuard],
            data: { module: 'tenant.events-report' },
            loadComponent: () =>
              import('./modules/reports/events/events-report.component').then(
                (m) => m.EventsReportComponent,
              ),
          },
          {
            path: 'financials',
            canActivate: [moduleAccessGuard],
            data: { module: 'tenant.financials-report' },
            loadComponent: () =>
              import(
                './modules/reports/financials/financials-report.component'
              ).then((m) => m.FinancialsReportComponent),
          },
        ],
      },
      {
        path: 'engagement',
        children: [
          {
            path: 'pipelines',
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.pipelines' },
            loadComponent: () =>
              import(
                './modules/engagement/pipelines/pipeline-boards-landing/pipeline-boards-landing.component'
              ).then((m) => m.PipelineBoardsLandingComponent),
          },
          {
            path: 'pipelines/:id',
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.pipelines' },
            loadComponent: () =>
              import(
                './modules/engagement/pipelines/pipelines.component'
              ).then((m) => m.PipelinesComponent),
          },
          {
            path: 'tasks',
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.tasks' },
            loadComponent: () =>
              import(
                './modules/engagement/work-queue/work-queue.component'
              ).then((m) => m.WorkQueueComponent),
          },
          {
            path: 'activities',
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.activities' },
            loadComponent: () =>
              import(
                './modules/engagement/activities/activities.component'
              ).then((m) => m.ActivitiesComponent),
          },
        ],
      },
      {
        path: 'groups',
        canActivate: [moduleAccessGuard],
        data: { module: 'chapter.groups' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/groups/groups.component').then(
                (m) => m.GroupsComponent,
              ),
          },
          {
            path: ':groupId',
            loadComponent: () =>
              import('./modules/groups/group-detail/group-detail.component').then(
                (m) => m.GroupDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'resources',
        loadComponent: () =>
          import('./modules/resources/resources.component').then(
            (m) => m.ResourcesComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { module: 'chapter.resources' },
      },
      {
        path: 'store',
        canActivate: [moduleAccessGuard, featureFlagGuard],
        // featureFlagGuard closes the direct-URL gap when the storefront module
        // is gated off — the nav entry is already locked.
        data: { module: 'tenant.storefront', featureFlag: 'storefront' },
        children: [
          {
            // Store overview (KPIs / recent activity) — the /store index.
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./modules/store/overview/storefront-overview.component').then(
                (m) => m.StorefrontOverviewComponent,
              ),
          },
          {
            // Storefront admin screens are authored in @ams/storefront; the host
            // app only lazy-loads them and wires guards (no storefront feature
            // code under apps/*).
            path: 'products',
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.ProductListPageComponent),
          },
          {
            // Creating a product is Manage on the API (StoreProductsController
            // .Create), and this page is a create form with nothing to view, so
            // it's gated at the route rather than read-only in-page.
            path: 'products/new',
            canActivate: [moduleAccessGuard, featureFlagGuard],
            data: { module: 'tenant.storefront', requiredLevel: 'manage', featureFlag: 'storefront' },
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.ProductCreatePageComponent),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'products/:id',
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.ProductEditPageComponent),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'categories',
            canActivate: [moduleAccessGuard, featureFlagGuard],
            data: { module: 'tenant.storefront', requiredLevel: 'edit', featureFlag: 'storefront' },
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.StorefrontCategoriesComponent),
          },
          {
            // Fees admin (#25924) — between Coupons and Shipping rates.
            path: 'fees',
            canActivate: [moduleAccessGuard, featureFlagGuard],
            data: { module: 'tenant.storefront', requiredLevel: 'edit', featureFlag: 'storefront' },
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.StorefrontFeesComponent),
          },
          {
            // Store settings (#21799) — writes config, so it requires Manage
            // access (not just View, unlike read-only Reports).
            path: 'settings',
            canActivate: [moduleAccessGuard, featureFlagGuard],
            data: { module: 'tenant.storefront', requiredLevel: 'manage', featureFlag: 'storefront' },
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.StoreSettingsPageComponent),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            // Shipping rates admin (#21796).
            path: 'shipping-rates',
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.ShippingRatesComponent),
          },
          {
            path: 'reports',
            loadComponent: () =>
              import('@ams/storefront/pages').then((m) => m.StorefrontReportsComponent),
          },
        ],
      },
      {
        path: 'cms',
        canActivate: [moduleAccessGuard],
        data: { module: 'chapter.cms' },
        children: [
          {
            path: 'models',
            loadComponent: () =>
              import('./modules/cms/models.component').then(
                (m) => m.CmsModelsComponent,
              ),
          },
          {
            path: 'models/new',
            loadComponent: () =>
              import('./modules/cms/model-editor/model-editor.component').then(
                (m) => m.CmsModelEditorComponent,
              ),
            // The editor routes re-state `module` rather than leaning on the parent's
            // view-level data, so the stricter level is the one that gets checked.
            // Model create/update are Manage on the API (CmsController), so a view- or
            // edit-only user must not reach the editor at all — by URL or otherwise.
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.cms', requiredLevel: 'manage' },
          },
          {
            path: 'models/:modelId/edit',
            loadComponent: () =>
              import('./modules/cms/model-editor/model-editor.component').then(
                (m) => m.CmsModelEditorComponent,
              ),
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.cms', requiredLevel: 'manage' },
          },
          {
            path: 'content',
            loadComponent: () =>
              import('./modules/cms/content.component').then(
                (m) => m.CmsContentComponent,
              ),
          },
          {
            path: 'content/new',
            loadComponent: () =>
              import('./modules/cms/content-editor/content-editor.component').then(
                (m) => m.CmsContentEditorComponent,
              ),
            // Content create/update are Edit on the API, not Manage — a lower bar than
            // models, so these two stay at 'edit'.
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.cms', requiredLevel: 'edit' },
          },
          {
            path: 'content/:contentId',
            loadComponent: () =>
              import('./modules/cms/content-detail/content-detail.component').then(
                (m) => m.CmsContentDetailComponent,
              ),
          },
          {
            path: 'content/:contentId/edit',
            loadComponent: () =>
              import('./modules/cms/content-editor/content-editor.component').then(
                (m) => m.CmsContentEditorComponent,
              ),
            canActivate: [moduleAccessGuard],
            data: { module: 'chapter.cms', requiredLevel: 'edit' },
          },
        ],
      },
      // Dashboard announcement/news/article widgets share one row-click route
      // with the member portal (/announcements/:id, /news/:id, /articles/:id).
      // The admin app has no such pages, so redirect those clicks to the CMS
      // content detail (same id). The detail page's back link uses history
      // (location.back), so it returns to the dashboard for a widget click-through.
      { path: 'announcements/:contentId', redirectTo: 'cms/content/:contentId' },
      { path: 'news/:contentId', redirectTo: 'cms/content/:contentId' },
      { path: 'articles/:contentId', redirectTo: 'cms/content/:contentId' },
      {
        path: 'billing',
        canActivate: [moduleAccessGuard, featureFlagGuard],
        // Tenant admins (tenant.billing) and chapter admins (chapter.billing)
        // both reach Billing; the API scopes the account/invoice rows to the
        // caller's active org so chapter admins see only their chapter's members.
        // featureFlagGuard closes the direct-URL gap when SaaSLogic/billing is
        // gated off (demo sandbox) — the nav entry is already locked.
        data: { modules: ['tenant.billing', 'chapter.billing'], featureFlag: 'saasLogicBilling' },
        children: [
          {
            path: 'accounts',
            loadComponent: () =>
              import('./modules/billing/accounts/accounts.component').then(
                (m) => m.AccountsComponent,
              ),
          },
          {
            path: 'accounts/:membershipId',
            loadComponent: () =>
              import('./modules/billing/accounts/account-detail.component').then(
                (m) => m.AccountDetailComponent,
              ),
          },
          {
            path: 'invoices',
            loadComponent: () =>
              import('./modules/billing/invoices/invoices.component').then(
                (m) => m.InvoicesComponent,
              ),
          },
          {
            // After 'invoices' so the list keeps the bare path; Angular matches
            // in order and ':invoiceId' would otherwise swallow nothing here,
            // but keeping them adjacent makes the pairing obvious.
            path: 'invoices/:invoiceId',
            loadComponent: () =>
              import('./modules/billing/invoices/invoice-detail.component').then(
                (m) => m.InvoiceDetailComponent,
              ),
          },
          {
            path: 'coupons',
            loadComponent: () =>
              import('./modules/billing/coupons/coupons.component').then(
                (m) => m.CouponsComponent,
              ),
          },
        ],
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./modules/users/users.component').then(
            (m) => m.UsersComponent,
          ),
        canActivate: [moduleAccessGuard],
        // Staff page: tenant admins (tenant.staff) and chapter admins
        // (chapter.staff) both manage their scope's staff.
        data: { modules: ['tenant.staff', 'chapter.staff'], requiredLevel: 'view' },
      },
      {
        path: 'users/:id',
        loadComponent: () =>
          import('./modules/users/user-detail/user-detail.component').then(
            (m) => m.UserDetailComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { modules: ['tenant.staff', 'chapter.staff'], requiredLevel: 'view' },
      },
      {
        path: 'my-account',
        loadComponent: () =>
          import('./modules/account/account.component').then(
            (m) => m.AccountComponent,
          ),
      },
      {
        path: 'notification-settings',
        loadComponent: () =>
          import('./modules/notification-settings/notification-settings.component').then(
            (m) => m.NotificationSettingsComponent,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./modules/notifications/all-notifications.component').then(
            (m) => m.AllNotificationsComponent,
          ),
      },
      {
        path: 'roles',
        loadComponent: () =>
          import('./modules/roles/roles.component').then(
            (m) => m.RolesComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { module: 'tenant.roles', requiredLevel: 'manage' },
      },
      {
        path: 'permissions',
        loadComponent: () =>
          import('./modules/permissions/permissions.component').then(
            (m) => m.PermissionsComponent,
          ),
        canActivate: [moduleAccessGuard],
        data: { module: 'tenant.roles', requiredLevel: 'manage' },
      },
      {
        path: 'customizations',
        canActivate: [moduleAccessGuard],
        data: { module: 'tenant.customizations', requiredLevel: 'manage' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/customizations/customizations.component').then(
                (m) => m.CustomizationsComponent,
              ),
          },
          {
            path: 'capacities',
            loadComponent: () =>
              import(
                './modules/customizations/capacities/capacities.component'
              ).then((m) => m.CapacitiesComponent),
          },
          {
            path: 'legal',
            loadComponent: () =>
              import(
                './modules/customizations/legal/customizations-legal.component'
              ).then((m) => m.CustomizationsLegalComponent),
          },
          {
            // Must stay above ':entityType', which would otherwise take "tags"
            // as an entity type. Guarded by the parent (tenant.customizations Manage).
            path: 'tags',
            loadComponent: () =>
              import('./modules/customizations/tags/tags.component').then(
                (m) => m.TagsComponent,
              ),
          },
          {
            path: ':entityType',
            loadComponent: () =>
              import(
                './modules/customizations/entity-fields/entity-fields.component'
              ).then((m) => m.EntityFieldsComponent),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: 'dashboard-admin',
        canActivate: [moduleAccessGuard],
        data: { modules: ['tenant.dashboard', 'chapter.dashboard'], requiredLevel: 'manage' },
        children: [
          {
            path: '',
            redirectTo: 'templates',
            pathMatch: 'full',
          },
          {
            path: 'templates',
            loadComponent: () =>
              import('./modules/dashboard-admin/dashboard-templates.component').then(
                (m) => m.DashboardTemplatesComponent,
              ),
          },
          {
            path: 'templates/:id',
            loadComponent: () =>
              import('./modules/dashboard-admin/template-editor.component').then(
                (m) => m.TemplateEditorComponent,
              ),
          },
          {
            path: 'widget-definitions',
            loadComponent: () =>
              import('./modules/dashboard-admin/widget-definitions.component').then(
                (m) => m.WidgetDefinitionsComponent,
              ),
          },
          {
            path: 'widget-definitions/:id',
            loadComponent: () =>
              import('./modules/dashboard-admin/widget-editor.component').then(
                (m) => m.WidgetEditorComponent,
              ),
          },
        ],
      },
      {
        path: 'settings',
        canActivate: [moduleAccessGuard],
        // View-level access is enough to OPEN a settings page — a role granted
        // tenant.settings/view is meant to read the configuration. Each page
        // gates its own write actions (Save, Connect/Disconnect, uploads) on
        // tenant.settings/manage — the level every Settings write endpoint
        // requires — so a view-only admin sees a read-only screen.
        data: { module: 'tenant.settings', requiredLevel: 'view' },
        children: [
          {
            path: 'general',
            loadComponent: () =>
              import('./modules/settings/general/general.component').then(
                (m) => m.GeneralComponent,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'branding',
            loadComponent: () =>
              import('./modules/settings/branding/branding.component').then(
                (m) => m.BrandingComponent,
              ),
          },
          {
            path: 'auth',
            loadComponent: () =>
              import('./modules/settings/auth/auth.component').then(
                (m) => m.AuthComponent,
              ),
          },
          {
            path: 'appearance',
            loadComponent: () =>
              import('./modules/settings/appearance/appearance.component').then(
                (m) => m.AppearanceComponent,
              ),
          },
          {
            path: 'components',
            loadComponent: () =>
              import('./modules/settings/components/components.component').then(
                (m) => m.ComponentsComponent,
              ),
          },
          {
            path: 'subscription',
            loadComponent: () =>
              import('./modules/settings/subscription/subscription.component').then(
                (m) => m.SubscriptionComponent,
              ),
          },
        ],
      },
      {
        path: '**',
        redirectTo: 'not-found',
      },
    ],
  },
];
